import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  APP_LANGUAGES,
  AppLanguage,
  DEFAULT_LANG,
  localeFor
} from '../i18n/app-languages';
import { environment } from '../../../environments/environment';

const LANG_STORAGE_KEY = 'app_lang';
const LANG_LIST_CACHE_KEY = 'app_lang_list';

/** Shape returned by GET /api/locales. */
interface RemoteLocale {
  code: string;
  nativeName: string;
  badge: string;
  bcpTag: string;
}

@Injectable({ providedIn: 'root' })
export class TranslationStore {
  private readonly translate = inject(TranslateService);
  private readonly http = inject(HttpClient);

  readonly currentLang = signal<string>(DEFAULT_LANG);

  /**
   * All selectable languages, for the switcher. Starts from the last-good backend list
   * (or the bundled list) and is replaced by a fresh backend list at boot. Never empty,
   * so the switcher and locale resolution always have something to fall back to.
   */
  readonly languages = signal<readonly AppLanguage[]>(this.cachedOrBundledLanguages());

  /** BCP-47 tag for the active language, for Intl date/number formatting. */
  readonly currentLocale = computed(() => this.localeForCode(this.currentLang()));

  readonly currentLanguage = computed(
    () => this.languages().find(l => l.code === this.currentLang()) ?? this.languages()[0]
  );

  /** Fetches the supported-language list from the backend; keeps the fallback list on failure. */
  async loadLanguages(): Promise<void> {
    try {
      const remote = await firstValueFrom(
        this.http.get<RemoteLocale[]>(`${environment.apiUrl}/locales`)
      );
      if (remote?.length) {
        const mapped: AppLanguage[] = remote.map(r => ({
          code: r.code,
          nativeName: r.nativeName,
          badge: r.badge,
          locale: r.bcpTag
        }));
        this.languages.set(mapped);
        this.writeCachedLanguages(mapped);
      }
    } catch {
      // Offline / API down: keep the cached or bundled list — never leave the app language-less.
    }
  }

  /**
   * Whether the admin has ever picked a language on this browser. Until they do, the app follows
   * the best guess available — the browser's language, or the invitation a new owner arrived
   * from — and a guess must never be mistaken for a choice: a choice is kept and synced to the
   * server, a guess is neither.
   */
  readonly hasExplicitChoice = signal(false);

  init(): void {
    this.translate.setDefaultLang(DEFAULT_LANG);
    // A stale code from a language no longer offered counts as no choice at all.
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    const chosen = this.isOffered(stored);
    this.hasExplicitChoice.set(chosen);
    // Before any choice, the browser's language: an owner opening the app for the first time
    // used to land on English regardless, with the switcher three screens away.
    this.apply(chosen ? stored! : this.browserLang());
  }

  changeLang(lang: string): void {
    const resolved = this.resolve(lang);
    localStorage.setItem(LANG_STORAGE_KEY, resolved);
    this.hasExplicitChoice.set(true);
    this.apply(resolved);
  }

  /**
   * A language suggested by context — the invitation a new owner is activating from was written
   * in it — applied only while the admin has not chosen one themselves, and never persisted:
   * it is a starting point for this visit, not a preference.
   */
  suggest(lang: string): void {
    if (this.hasExplicitChoice()) return;
    this.apply(this.resolve(lang));
  }

  private apply(lang: string): void {
    this.translate.use(lang);
    this.currentLang.set(lang);
  }

  private resolve(code: string | null | undefined): string {
    return this.isOffered(code) ? (code as string) : DEFAULT_LANG;
  }

  private isOffered(code: string | null | undefined): boolean {
    return !!code && this.languages().some(l => l.code === code);
  }

  /** The first browser language the app ships, by primary subtag ("ro-RO" → "ro"); else the default. */
  private browserLang(): string {
    const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const tag of tags) {
      const code = tag?.split('-')[0]?.toLowerCase();
      if (this.isOffered(code)) return code;
    }
    return DEFAULT_LANG;
  }

  private localeForCode(code: string): string {
    return this.languages().find(l => l.code === code)?.locale ?? localeFor(code);
  }

  private cachedOrBundledLanguages(): readonly AppLanguage[] {
    try {
      const raw = localStorage.getItem(LANG_LIST_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          return parsed as AppLanguage[];
        }
      }
    } catch {
      // Corrupt cache — fall through to the bundled list.
    }
    return APP_LANGUAGES;
  }

  private writeCachedLanguages(languages: AppLanguage[]): void {
    try {
      localStorage.setItem(LANG_LIST_CACHE_KEY, JSON.stringify(languages));
    } catch {
      // Non-fatal; the list still lives in memory this session.
    }
  }
}
